import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, InputAdornment,
  LinearProgress, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add, Delete, Edit, SwapHoriz } from '@mui/icons-material';
import api from '../../services/api';
import type { Machine, MachineSchedule, SaveMachineSchedule, ScheduleForecast } from '../../types/factory';
import { EmptyState, Kpi, KpiStrip, PageHeader, ProductSelect, Section, StatusPill, addDaysIso, errorMessage, fmtPct, fmtQty, isoDay, num, todayIso } from './shared';

// Plan recurrente de máquinas (reunión con Asener 03/11/2026): las inyectoras no se cortan y las matrices se cambian
// los lunes, así que se planifica por tramos ("esta máquina con esta matriz hasta tal fecha") y no día por día. Lo
// esperado sale por tiempo y se compara con lo pesado en lotes (o los partes, si la máquina no lotea).

const WEEKDAYS = [
  { value: 1, label: 'L' }, { value: 2, label: 'M' }, { value: 3, label: 'X' }, { value: 4, label: 'J' },
  { value: 5, label: 'V' }, { value: 6, label: 'S' }, { value: 7, label: 'D' },
];

const isMold = (m: Machine) => m.kind === 'Matriz';
const dayLabel = (iso: string) => {
  const d = new Date(`${iso}T12:00:00Z`);
  return `${['D', 'L', 'M', 'X', 'J', 'V', 'S'][d.getUTCDay()]} ${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
const fmtDate = (iso?: string | null) => (iso ? isoDay(iso).split('-').reverse().join('/') : '');
const daysText = (days: string) => {
  const set = days.split(',').map(Number);
  if (set.join(',') === '1,2,3,4,5') return 'lunes a viernes';
  if (set.join(',') === '1,2,3,4,5,6') return 'lunes a sábado';
  if (set.length === 7) return 'todos los días';
  return WEEKDAYS.filter(w => set.includes(w.value)).map(w => w.label).join(' ');
};

/** Próximo lunes (o hoy si es lunes): es cuando se cambian las matrices. */
function nextMonday(): string {
  const today = todayIso();
  const dow = new Date(`${today}T12:00:00Z`).getUTCDay();
  return addDaysIso(today, dow === 1 ? 0 : (8 - dow) % 7 || 7);
}

const FactorySchedulePage: React.FC = () => {
  const [machines, setMachines] = useState<Machine[]>([]);
  const [schedules, setSchedules] = useState<MachineSchedule[] | null>(null);
  const [forecast, setForecast] = useState<ScheduleForecast | null>(null);
  const [range, setRange] = useState({ from: addDaysIso(todayIso(), -7), to: addDaysIso(todayIso(), 14) });
  const [form, setForm] = useState<ScheduleForm | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [m, s, f] = await Promise.all([api.getMachines(), api.getMachineSchedules(), api.getScheduleForecast(range)]);
    setMachines(m);
    setSchedules(s);
    setForecast(f);
  }, [range]);
  useEffect(() => { load().catch(e => setError(errorMessage(e, 'No se pudo cargar el plan'))); }, [load]);

  const hosts = machines.filter(m => !isMold(m));
  const molds = machines.filter(isMold);
  const today = todayIso();

  const current = (machineId: number) => (schedules || []).find(s => s.machineId === machineId && isoDay(s.from) <= today && (!s.to || isoDay(s.to) >= today));
  const upcoming = (machineId: number) => (schedules || []).filter(s => s.machineId === machineId && isoDay(s.from) > today);

  const remove = async (s: MachineSchedule) => {
    if (!window.confirm(`¿Borrar el tramo de ${s.machineName} desde el ${fmtDate(s.from)}?`)) return;
    try { await api.deleteMachineSchedule(s.id); load(); } catch (e) { setError(errorMessage(e, 'No se pudo borrar')); }
  };

  const totals = useMemo(() => {
    const past = (forecast?.days || []).filter(d => isoDay(d.date) < today && d.expectedUnits != null);
    const expected = past.reduce((s, d) => s + (d.expectedUnits || 0), 0);
    const actual = past.reduce((s, d) => s + (d.actualUnits || 0), 0);
    const ahead = (forecast?.days || []).filter(d => isoDay(d.date) >= today).reduce((s, d) => s + (d.expectedUnits || 0), 0);
    return { expected, actual, ahead, pct: expected > 0 ? actual / expected : null };
  }, [forecast, today]);

  return (
    <Box>
      <PageHeader
        title="Plan de máquinas"
        description={<>Qué matriz lleva cada máquina y qué produce, por tramos: las inyectoras no se cortan y las matrices se cambian los lunes. Lo esperado sale por tiempo (piezas por hora × horas) y se compara con lo que se pesó.</>}
        actions={<Button variant="contained" startIcon={<Add />} onClick={() => setForm(newForm())} disabled={hosts.length === 0}>Nuevo tramo</Button>}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <KpiStrip>
        <Kpi label="Esperado hasta ayer" value={fmtQty(totals.expected)} hint={`desde el ${fmtDate(range.from)}`} />
        <Kpi label="Salió" value={fmtQty(totals.actual)} hint="lotes pesados o partes" />
        <Kpi label="Cumplimiento" value={fmtPct(totals.pct)} tone={totals.pct == null ? undefined : totals.pct >= 0.9 ? 'ok' : totals.pct >= 0.7 ? 'warn' : 'bad'} />
        <Kpi label="Esperado de hoy en adelante" value={fmtQty(totals.ahead)} hint={`hasta el ${fmtDate(range.to)}`} />
      </KpiStrip>

      <Section title="Qué tiene cada máquina" description="El tramo vigente y los cambios ya programados.">
        {!schedules ? <LinearProgress /> : hosts.length === 0 ? (
          <EmptyState title="No hay máquinas cargadas" hint="Se cargan en Producción → Máquinas y matrices." />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Máquina</TableCell>
                  <TableCell>Matriz · producto</TableCell>
                  <TableCell>Desde · hasta</TableCell>
                  <TableCell align="right">Por día</TableCell>
                  <TableCell>Próximo cambio</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {hosts.map(m => {
                  const s = current(m.id);
                  const next = upcoming(m.id)[0];
                  return (
                    <TableRow key={m.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{m.name}</TableCell>
                      <TableCell>
                        {s ? <>{s.moldName || 'sin matriz'} · {s.productName}</> : <StatusPill tone="warn">Sin plan</StatusPill>}
                      </TableCell>
                      <TableCell>{s ? `${fmtDate(s.from)} → ${s.to ? fmtDate(s.to) : 'sigue'}` : '—'}{s && <Typography variant="caption" color="text.secondary" display="block">{s.hoursPerDay} h · {daysText(s.days)}</Typography>}</TableCell>
                      <TableCell align="right">{s?.unitsPerDay != null ? fmtQty(s.unitsPerDay) : s ? <Tooltip title="La matriz y la máquina no tienen piezas por hora cargadas"><span>—</span></Tooltip> : '—'}</TableCell>
                      <TableCell>{next ? `${fmtDate(next.from)}: ${next.moldName || 'sin matriz'} · ${next.productName}` : '—'}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Button size="small" startIcon={<SwapHoriz />} onClick={() => setForm({ ...newForm(), machineId: m.id, productId: s?.productId ?? null, hoursPerDay: String(s?.hoursPerDay ?? 24), days: s?.days ?? '1,2,3,4,5' })}>
                          Cambiar matriz
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Section>

      <Section
        title="Esperado contra real"
        description="Cada celda: lo que salió / lo esperado. Los días que vienen muestran solo lo esperado."
        actions={<Stack direction="row" spacing={1}>
          <TextField size="small" type="date" label="Desde" value={range.from} onChange={e => setRange({ ...range, from: e.target.value })} InputLabelProps={{ shrink: true }} />
          <TextField size="small" type="date" label="Hasta" value={range.to} onChange={e => setRange({ ...range, to: e.target.value })} InputLabelProps={{ shrink: true }} />
        </Stack>}
      >
        {!forecast ? <LinearProgress /> : <ForecastGrid forecast={forecast} today={today} />}
      </Section>

      {forecast && forecast.products.length > 0 && (
        <Section title="Por producto" description="Lo esperado en todo el período contra lo que ya salió.">
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow><TableCell>Producto</TableCell><TableCell align="right">Esperado</TableCell><TableCell align="right">Salió</TableCell></TableRow></TableHead>
              <TableBody>
                {forecast.products.map(p => (
                  <TableRow key={p.productId}><TableCell>{p.productName}</TableCell><TableCell align="right">{fmtQty(p.expectedUnits)}</TableCell><TableCell align="right">{fmtQty(p.actualUnits)}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Section>
      )}

      <Section title="Tramos cargados" description="Vigentes y futuros.">
        {!schedules ? <LinearProgress /> : schedules.length === 0 ? (
          <EmptyState title="Todavía no hay plan" hint="Cargá qué matriz tiene cada inyectora: con un tramo sin fecha de fin alcanza hasta el próximo cambio." />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Máquina</TableCell><TableCell>Matriz</TableCell><TableCell>Producto</TableCell><TableCell>Desde</TableCell><TableCell>Hasta</TableCell><TableCell>Horario</TableCell><TableCell align="right">Por día</TableCell><TableCell /></TableRow>
              </TableHead>
              <TableBody>
                {schedules.map(s => (
                  <TableRow key={s.id} hover>
                    <TableCell>{s.machineName}</TableCell>
                    <TableCell>{s.moldName || '—'}</TableCell>
                    <TableCell>{s.productName}</TableCell>
                    <TableCell>{fmtDate(s.from)}</TableCell>
                    <TableCell>{s.to ? fmtDate(s.to) : 'sigue'}</TableCell>
                    <TableCell>{s.hoursPerDay} h · {daysText(s.days)}</TableCell>
                    <TableCell align="right">{fmtQty(s.unitsPerDay)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <IconButton size="small" onClick={() => setForm(toForm(s))}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => remove(s)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Section>

      {form && <ScheduleDialog form={form} hosts={hosts} molds={molds} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
    </Box>
  );
};

// ==================== Grilla esperado / real ====================

const ForecastGrid: React.FC<{ forecast: ScheduleForecast; today: string }> = ({ forecast, today }) => {
  const dates: string[] = [];
  for (let d = isoDay(forecast.from); d <= isoDay(forecast.to); d = addDaysIso(d, 1)) dates.push(d);
  const machines = Array.from(new Map(forecast.days.map(d => [d.machineId, d.machineName])).entries());
  if (machines.length === 0) return <EmptyState title="Nada planificado ni producido en estas fechas" />;
  const cell = (machineId: number, date: string) => forecast.days.find(d => d.machineId === machineId && isoDay(d.date) === date);
  return (
    <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
      <Table size="small" sx={{ '& td, & th': { px: 1, whiteSpace: 'nowrap' } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>Máquina</TableCell>
            {dates.map(d => <TableCell key={d} align="center" sx={{ fontWeight: d === today ? 700 : undefined, color: d === today ? 'primary.main' : undefined }}>{dayLabel(d)}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {machines.map(([id, name]) => (
            <TableRow key={id}>
              <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1, fontWeight: 600 }}>{name}</TableCell>
              {dates.map(d => {
                const c = cell(id, d);
                if (!c) return <TableCell key={d} align="center" sx={{ color: 'text.disabled' }}>·</TableCell>;
                const past = d < today;
                const ratio = c.expectedUnits && c.actualUnits != null ? c.actualUnits / c.expectedUnits : null;
                const bg = !past || ratio == null ? undefined : ratio >= 0.9 ? 'rgba(46,125,50,.10)' : ratio >= 0.7 ? 'rgba(237,108,2,.12)' : 'rgba(211,47,47,.10)';
                return (
                  <TableCell key={d} align="center" sx={{ bgcolor: bg, fontSize: 12 }}>
                    <Tooltip title={`${c.productName || 'Sin plan'}${c.moldName ? ` · ${c.moldName}` : ''}${c.actualSource ? ` · real según ${c.actualSource}` : ''}`}>
                      <span>
                        {past || d === today
                          ? <>{c.actualUnits != null ? fmtQty(c.actualUnits) : '—'}<Typography component="span" variant="caption" color="text.secondary"> / {c.expectedUnits != null ? fmtQty(c.expectedUnits) : '—'}</Typography></>
                          : <Typography component="span" variant="caption" color="text.secondary">{c.expectedUnits != null ? fmtQty(c.expectedUnits) : c.hours ? '?' : 'para'}</Typography>}
                      </span>
                    </Tooltip>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ==================== Alta / edición ====================

type ScheduleForm = { id?: number; machineId: number | null; moldId: number | null; productId: number | null; from: string; to: string; openEnded: boolean; hoursPerDay: string; days: string; notes: string };

const newForm = (): ScheduleForm => ({ machineId: null, moldId: null, productId: null, from: nextMonday(), to: '', openEnded: true, hoursPerDay: '24', days: '1,2,3,4,5', notes: '' });
const toForm = (s: MachineSchedule): ScheduleForm => ({
  id: s.id, machineId: s.machineId, moldId: s.moldId ?? null, productId: s.productId, from: isoDay(s.from), to: s.to ? isoDay(s.to) : '',
  openEnded: !s.to, hoursPerDay: String(s.hoursPerDay), days: s.days, notes: s.notes || '',
});

const ScheduleDialog: React.FC<{ form: ScheduleForm; hosts: Machine[]; molds: Machine[]; onClose: () => void; onSaved: () => void }> = ({ form: initial, hosts, molds, onClose, onSaved }) => {
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const days = form.days.split(',').filter(Boolean).map(Number);
  const mold = molds.find(m => m.id === form.moldId);
  const machine = hosts.find(m => m.id === form.machineId);
  const uph = mold?.effectiveUnitsPerHour ?? machine?.effectiveUnitsPerHour ?? null;
  const save = async () => {
    if (!form.machineId || !form.productId) return;
    const body: SaveMachineSchedule = {
      machineId: form.machineId, moldId: form.moldId, productId: form.productId, from: form.from, to: form.openEnded ? null : form.to || null,
      hoursPerDay: num(form.hoursPerDay), days: form.days, notes: form.notes || null,
    };
    try {
      if (form.id) await api.updateMachineSchedule(form.id, body); else await api.createMachineSchedule(body);
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo guardar'));
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{form.id ? 'Editar tramo' : 'Nuevo tramo del plan'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {!form.id && <Typography variant="body2" color="text.secondary">Si la máquina tiene un tramo abierto, termina el día anterior a este.</Typography>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField size="small" select fullWidth label="Máquina" value={form.machineId ?? ''} onChange={e => setForm({ ...form, machineId: Number(e.target.value) })} required>
              {hosts.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
            </TextField>
            <TextField size="small" select fullWidth label="Matriz" value={form.moldId ?? ''} onChange={e => setForm({ ...form, moldId: e.target.value === '' ? null : Number(e.target.value) })}>
              <MenuItem value="">Sin matriz</MenuItem>
              {molds.map(m => <MenuItem key={m.id} value={m.id}>{m.name}{m.status !== 'Disponible' ? ` (${m.status})` : ''}</MenuItem>)}
            </TextField>
          </Stack>
          <ProductSelect label="Qué produce" value={form.productId} onChange={id => setForm({ ...form, productId: id })} required />
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField size="small" type="date" label="Desde" value={form.from} onChange={e => setForm({ ...form, from: e.target.value })} InputLabelProps={{ shrink: true }} />
            {!form.openEnded && <TextField size="small" type="date" label="Hasta" value={form.to} onChange={e => setForm({ ...form, to: e.target.value })} InputLabelProps={{ shrink: true }} />}
            <FormControlLabel control={<Checkbox checked={form.openEnded} onChange={e => setForm({ ...form, openEnded: e.target.checked })} />} label="Hasta que se cambie" />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField size="small" label="Horas por día" value={form.hoursPerDay} onChange={e => setForm({ ...form, hoursPerDay: e.target.value })} sx={{ width: 140 }}
              InputProps={{ endAdornment: <InputAdornment position="end">h</InputAdornment> }} />
            <Stack direction="row" spacing={0.5}>
              {WEEKDAYS.map(w => (
                <Button key={w.value} size="small" variant={days.includes(w.value) ? 'contained' : 'outlined'} sx={{ minWidth: 34, px: 0 }}
                  onClick={() => setForm({ ...form, days: (days.includes(w.value) ? days.filter(x => x !== w.value) : [...days, w.value]).sort().join(',') })}>
                  {w.label}
                </Button>
              ))}
            </Stack>
          </Stack>
          <Typography variant="body2">
            {uph ? <>Esperado: <b>{fmtQty(uph * num(form.hoursPerDay))} piezas por día</b> ({fmtQty(uph)} por hora{mold?.effectiveUnitsPerHour ? ' de la matriz' : ' de la máquina'}).</>
              : <span style={{ color: '#888' }}>La matriz y la máquina no tienen piezas por hora: cargalas en Máquinas y matrices para ver lo esperado.</span>}
          </Typography>
          <TextField size="small" label="Notas" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={!form.machineId || !form.productId || !form.from || (!form.openEnded && !form.to) || days.length === 0}>Guardar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FactorySchedulePage;
